"use client";

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useForm, FormProvider } from 'react-hook-form';
import {
    Container,
    Typography,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Breadcrumb,
    Button,
    Badge,
    DataDisplay,
} from '@/components/design-system';
import {
    ChevronDown,
    ChevronUp,
    ArrowRight,
    Sparkles,
    Zap,
    Star,
    TrendingUp,
    Palette,
    Layers,
} from 'lucide-react';
import { ICategory } from '@/constants/categories';
import { useLocaleNavigation } from '@/lib/navigation';
import { useLocale, useTranslations } from 'next-intl';  

export interface GeneratorPageProps {
    // Hero section data
    title: string;
    description: string;
    icon: React.ReactNode;
    category: ICategory;
    children: React.ReactNode;
    // Generator form data
    generatedData: string | null;
    isGenerating: boolean;
    onGenerate: (e: React.FormEvent, data?: any) => void;
    generateButtonText?: string;

    // Custom results rendering (optional)
    // If provided, this will be rendered in the results section instead of the default DataDisplay
    // The results section automatically appears when generatedData is available and scrolls into view
    resultsRenderer?: React.ReactNode;

    // Form configuration options (optional)
    formConfig?: {
        fields: {
            label: string;
            type: 'text' | 'number' | 'select' | 'checkbox';
            key: string;
            placeholder?: string;
            options?: { label: string; value: string }[];
            defaultValue?: any;
            validation?: {
                required?: boolean;
                min?: number;
                max?: number;
                minLength?: number;
                maxLength?: number;
                pattern?: RegExp;
                validate?: (value: any) => boolean | string;
            };
        }[];
        values: Record<string, any>;
        onChange: (key: string, value: any) => void;
    };

    // FAQ data
    faqs: {
        question: string;
        answer: string;
    }[];

    // Similar generators data
    similarGenerators: {
        id: string;
        name: string;
        description: string;
        icon: React.ReactNode;
        href: string;
        category: string;
    }[];

    // SEO and metadata
    metadata?: {
        keywords?: string[];
        examples?: string[];
        useCases?: string[];
    };
}

const GeneratorPage: React.FC<GeneratorPageProps> = ({
    title,
    description,
    category,
    children,
    generatedData,
    isGenerating,
    onGenerate,
    generateButtonText = "Generate",
    resultsRenderer,
    formConfig,
    faqs,
    similarGenerators,
    metadata,
}) => {
    const { getNavigationPath } = useLocaleNavigation();
    const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
    const resultsSectionRef = useRef<HTMLDivElement>(null);
    const locale = useLocale();
    const t = useTranslations('generatorPage');
    // Initialize react-hook-form
    const methods = useForm({
        defaultValues: formConfig?.values || {},
        mode: 'onChange', // Validate on change for better UX
    });
    
    const { handleSubmit, formState: { errors, isValid }, trigger } = methods;
    
    const breadcrumbs = [
        { label: category.name[locale], href: getNavigationPath(category.id) },
        { label: title, isActive: true },
    ];

    // Auto-scroll to results when data is generated
    useEffect(() => {
        if (generatedData && resultsSectionRef.current) {
            setTimeout(() => {
                resultsSectionRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }, 500); // Small delay to ensure the results section is rendered
        }
    }, [generatedData]);

    const toggleFaq = (index: number) => {
        setExpandedFaq(expandedFaq === index ? null : index);
    };

    // Enhanced form submission handler
    const onSubmit = (data: any) => {
        // This will only be called if form validation passes
        const mockEvent = new Event('submit') as any;
        onGenerate(mockEvent, data);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/10">
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-background via-background to-muted/20">
                {/* Enhanced background decorative elements */}
                <div className="absolute inset-0 bg-grid-pattern opacity-5" />
                <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-3xl animate-pulse" />
                <div className="absolute bottom-0 left-0 w-72 h-72 bg-gradient-to-br from-secondary/10 to-transparent rounded-full blur-3xl animate-pulse" />
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-primary/5 via-secondary/5 to-primary/5 rounded-full blur-3xl opacity-30" />

                <Container className="relative">
                    <div className="py-12">
                        <div className="max-w-4xl mx-auto">
                            {/* Enhanced Breadcrumb */}
                            <div className="mb-8 animate-in fade-in-0 duration-1000 delay-100">
                                <Breadcrumb items={breadcrumbs} className="justify-center" />
                            </div>

                            {/* Enhanced Hero content */}
                            <div className="text-center space-y-8">
                                <div className="space-y-6">
                                    <Typography
                                        variant="h1"
                                        className="bg-gradient-to-r from-foreground via-foreground/90 to-foreground/70 bg-clip-text text-transparent animate-in fade-in-0 duration-1000 delay-200"
                                    >
                                        {title}
                                    </Typography>

                                    <Typography
                                        variant="lead"
                                        className="max-w-2xl mx-auto text-muted-foreground animate-in fade-in-0 duration-1000 delay-300"
                                    >
                                        {description}
                                    </Typography>
                                </div>

                                {/* Enhanced stats with better styling */}
                                {metadata && (
                                    <div className="flex items-center justify-center gap-8 pt-8 animate-in fade-in-0 duration-1000 delay-400">
                                        {metadata.keywords && (
                                            <div className="text-center group">
                                                <div className="w-16 h-16 bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-xl flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform">
                                                    <Zap className="w-6 h-6 text-blue-500" />
                                                </div>
                                                <Typography variant="small" className="text-muted-foreground mb-1">
                                                    {t('stats.formatTypes')}
                                                </Typography>
                                                <Typography variant="large" className="font-bold text-blue-600">
                                                    {metadata.keywords.length}+
                                                </Typography>
                                            </div>
                                        )}
                                        {metadata.examples && (
                                            <div className="text-center group">
                                                <div className="w-16 h-16 bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-xl flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform">
                                                    <Star className="w-6 h-6 text-green-500" />
                                                </div>
                                                <Typography variant="small" className="text-muted-foreground mb-1">
                                                    {t('stats.examples')}
                                                </Typography>
                                                <Typography variant="large" className="font-bold text-green-600">
                                                    {metadata.examples.length}+
                                                </Typography>
                                            </div>
                                        )}
                                        {metadata.useCases && (
                                            <div className="text-center group">
                                                <div className="w-16 h-16 bg-gradient-to-br from-purple-500/10 to-purple-600/10 rounded-xl flex items-center justify-center mb-3 mx-auto group-hover:scale-110 transition-transform">
                                                    <Palette className="w-6 h-6 text-purple-500" />
                                                </div>
                                                <Typography variant="small" className="text-muted-foreground mb-1">
                                                    {t('stats.useCases')}
                                                </Typography>
                                                <Typography variant="large" className="font-bold text-purple-600">
                                                    {metadata.useCases.length}+
                                                </Typography>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </Container>
            </section>

            {/* Enhanced Generator Form Section */}
            <section className="py-16">
                <Container>
                    <div className="max-w-5xl mx-auto">
                        <Card className="relative overflow-hidden border-0 shadow-2xl bg-gradient-to-br from-card via-card/95 to-card/90 backdrop-blur-xl">
                            {/* Decorative background elements */}
                            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5" />
                            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-2xl" />
                            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-secondary/10 to-transparent rounded-full blur-2xl" />
                            
                            <CardHeader className="relative pb-8 border-b border-border/20">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl flex items-center justify-center shadow-lg">
                                        <Palette className="w-6 h-6 text-primary" />
                                    </div>
                                    <div className="space-y-2">
                                        <CardTitle className="text-2xl font-bold bg-gradient-to-r from-foreground via-foreground/90 to-foreground/70 bg-clip-text text-transparent leading-tight">
                                            {t('configuration.title')}
                                        </CardTitle>
                                        <Typography variant="small" className="text-muted-foreground font-medium">
                                            {t('configuration.description')}
                                        </Typography>
                                    </div>
                                </div>
                            </CardHeader>
                            
                            <CardContent className="relative p-8">
                                <FormProvider {...methods}>
                                    <form className="space-y-8" onSubmit={handleSubmit(onSubmit)}>
                                        {children}
                                        
                                        {/* Enhanced Generate Button Section */}
                                        <div className="flex flex-col items-center gap-6 pt-8">
                                            <Button
                                                type="submit"
                                                disabled={isGenerating || (formConfig && !isValid)}
                                                size="lg"
                                                className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-blue-500 to-indigo-600 hover:from-blue-500 hover:via-blue-400 hover:to-indigo-500 text-white font-bold px-16 py-6 rounded-2xl shadow-2xl hover:shadow-blue-500/30 transition-all duration-300 transform hover:scale-105 hover:-translate-y-1 text-lg cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                                            >
                                                {/* Button background animation */}
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 via-white/10 to-white/20 animate-pulse opacity-0 hover:opacity-100 transition-opacity duration-500" />
                                                
                                                <div className="relative flex items-center gap-3">
                                                    {isGenerating ? (
                                                        <>
                                                            <div className="animate-spin rounded-full h-6 w-6 border-3 border-white border-t-transparent" />
                                                            <span>{t('generateButton.generating')}</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Zap className="w-6 h-6" />
                                                            <span>{generateButtonText || t('generateButton.default')}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </Button>
                                            
                                            {/* Form validation status indicator */}
                                            {formConfig && Object.keys(errors).length > 0 && (
                                                <div className="text-center">
                                                    <Typography variant="small" className="text-red-500">
                                                        {t('generateButton.validationError')}
                                                    </Typography>
                                                </div>
                                            )}
                                        </div>
                                    </form>
                                </FormProvider>
                            </CardContent>
                        </Card>
                    </div>
                </Container>
            </section>

            {/* Enhanced Results Section */}
            {(generatedData || isGenerating) && (
                <section ref={resultsSectionRef} className="py-16 bg-gradient-to-br from-muted/5 via-background to-muted/10 relative overflow-hidden">
                    {/* Background decorative elements */}
                    <div className="absolute inset-0 bg-grid-pattern opacity-3" />
                    <div className="absolute top-0 right-1/4 w-64 h-64 bg-gradient-to-br from-green-500/5 to-transparent rounded-full blur-3xl" />
                    <div className="absolute bottom-0 left-1/4 w-48 h-48 bg-gradient-to-br from-blue-500/5 to-transparent rounded-full blur-3xl" />
                    
                    <Container className="relative">
                        <div className="max-w-5xl mx-auto">
                            {/* Enhanced Results Card */}
                            <Card className="relative overflow-hidden border-0 shadow-2xl bg-gradient-to-br from-card via-card/95 to-card/90 backdrop-blur-xl">
                                {/* Decorative background elements */}
                                <div className="absolute inset-0 bg-gradient-to-br from-green-500/5 via-transparent to-blue-500/5" />
                                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-green-500/10 to-transparent rounded-full blur-2xl" />
                                <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full blur-2xl" />
                                
                                <CardHeader className="relative pb-6 border-b border-border/20">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-xl flex items-center justify-center">
                                            <Layers className="w-5 h-5 text-green-500" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                                                {t('results.title')}
                                            </CardTitle>
                                            <Typography variant="small" className="text-muted-foreground">
                                                {t('results.description')}
                                            </Typography>
                                        </div>
                                    </div>
                                </CardHeader>
                                
                                <CardContent className="relative p-8">
                                    <div className="animate-in fade-in-0 duration-700">
                                        {resultsRenderer ? (
                                            // Custom results renderer
                                            <div className="space-y-6">
                                                {resultsRenderer}
                                            </div>
                                        ) : (
                                            // Default results display
                                            <div className="bg-gradient-to-br from-muted/10 via-muted/5 to-muted/10 rounded-2xl p-6 border border-border/20">
                                            <DataDisplay
                                                data={generatedData}
                                                loading={isGenerating}
                                                label={t('results.label')}
                                                placeholder={t('results.placeholder')}
                                                showCopy={true}
                                            />
                                            </div>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </Container>
                </section>
            )}

            {/* Redesigned FAQ Section */}
            <section className="py-20 bg-gradient-to-br from-muted/10 via-background to-muted/20 relative overflow-hidden">
                {/* Background decorative elements */}
                <div className="absolute inset-0 bg-grid-pattern opacity-3" />
                <div className="absolute top-0 left-1/4 w-64 h-64 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-3xl" />
                <div className="absolute bottom-0 right-1/4 w-48 h-48 bg-gradient-to-br from-secondary/5 to-transparent rounded-full blur-3xl" />
                
                <Container className="relative">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-16">
                            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/10 to-secondary/10 px-6 py-3 rounded-full mb-6 border border-primary/20">
                                <Sparkles className="w-5 h-5 text-primary" />
                                <Typography variant="small" className="font-semibold text-primary">
                                    {t('faq.badge')}
                                </Typography>
                            </div>
                            <Typography variant="h2" className="mb-6 bg-gradient-to-r from-foreground via-foreground/90 to-foreground/70 bg-clip-text text-transparent">
                                {t('faq.title')}
                            </Typography>
                            <Typography variant="lead" className="text-muted-foreground max-w-3xl mx-auto">
                                {t('faq.description', { title: title.toLowerCase() })}
                            </Typography>
                        </div>

                        <div className="space-y-4">
                            {faqs && faqs.length > 0 && faqs.map((faq, index) => (
                                <div 
                                    key={index}
                                    className="group relative"
                                >
                                    <div 
                                        onClick={() => toggleFaq(index)}
                                        className="relative overflow-hidden rounded-2xl border border-border/20 bg-gradient-to-br from-background/50 via-background to-background/80 backdrop-blur-sm hover:border-primary/30 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10 cursor-pointer"
                                    >
                                        {/* Subtle background decoration */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                        
                                        <div className="w-full p-6 text-left hover:bg-gradient-to-r hover:from-primary/5 hover:to-secondary/5 transition-all duration-300 rounded-2xl group">
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1 min-w-0">
                                                    <Typography variant="h5" className="font-semibold text-left leading-tight group-hover:text-primary transition-colors duration-300 pr-4">
                                                        {faq.question}
                                                    </Typography>
                                                </div>
                                                <div className="flex-shrink-0">
                                                    <div className="w-10 h-10 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300 group-hover:scale-110">
                                                        {expandedFaq === index ? (
                                                            <ChevronUp className="h-5 w-5 text-primary transition-all duration-300" />
                                                        ) : (
                                                            <ChevronDown className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-all duration-300" />
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {expandedFaq === index && (
                                                <div className="animate-in fade-in-0 duration-500 mt-6">
                                                    <div className="pt-6 border-t border-border/30">
                                                        <Typography variant="p" className="text-muted-foreground leading-relaxed">
                                                            {faq.answer}
                                                        </Typography>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Subtle accent line */}
                                        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/20 via-secondary/20 to-primary/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </Container>
            </section>

            {/* Enhanced Similar Generators Section */}
            {similarGenerators.length > 0 && (
                <section className="py-16">
                    <Container>
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center mb-12">
                                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-primary/10 to-secondary/10 px-6 py-3 rounded-full mb-6 border border-primary/20">
                                    <Sparkles className="w-5 h-5 text-primary" />
                                    <Typography variant="small" className="font-semibold text-primary">
                                        {t('similarGenerators.badge')}
                                    </Typography>
                                </div>
                                <Typography variant="h2" className="mb-4 bg-gradient-to-r from-foreground to-foreground/80 bg-clip-text text-transparent">
                                    {t('similarGenerators.title')}
                                </Typography>
                                <Typography variant="lead" className="text-muted-foreground max-w-2xl mx-auto">
                                    {t('similarGenerators.description')}
                                </Typography>
                            </div>

                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {similarGenerators.map((generator) => (
                                    <Link key={generator.id} href={generator.href} className="group">
                                        <Card 
                                            className="relative h-full hover:shadow-2xl transition-all duration-500 group-hover:border-primary/50 border-0 bg-gradient-to-br from-card to-card/80 backdrop-blur-sm group-hover:scale-105 overflow-hidden"
                                        >
                                            {/* Card background decoration */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                            
                                            <CardContent className="relative p-8">
                                                <div className="flex items-start justify-between mb-6">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-14 h-14 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-2xl flex items-center justify-center text-primary group-hover:bg-gradient-to-br group-hover:from-primary/30 group-hover:to-secondary/30 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
                                                            <div className="w-7 h-7 flex items-center justify-center">
                                                                {generator.icon}
                                                            </div>
                                                        </div>
                                                        <div>
                                                            <Typography variant="h5" className="font-semibold group-hover:text-primary transition-colors mb-2">
                                                                {generator.name}
                                                            </Typography>
                                                            <Badge variant="outline" className="text-xs border-primary/20 text-primary">
                                                                {generator.category}
                                                            </Badge>
                                                        </div>
                                                    </div>
                                                    <div className="w-10 h-10 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-primary/20 group-hover:to-secondary/20 transition-all duration-300 group-hover:scale-110">
                                                        <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-all duration-300 group-hover:translate-x-1" />
                                                    </div>
                                                </div>
                                                <Typography variant="p" className="text-sm text-muted-foreground leading-relaxed">
                                                    {generator.description[locale]}
                                                </Typography>
                                                
                                                {/* Hover indicator */}
                                                <div className="flex items-center gap-2 mt-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                                    <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full" />
                                                    <Typography variant="small" className="text-primary font-medium">
                                                        {t('similarGenerators.exploreGenerator')}
                                                    </Typography>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    </Container>
                </section>
            )}
        </div>
    );
};

export default GeneratorPage;
